import {
  CheckOutlined,
  ChevronLeft,
  ChevronRight,
  Close,
  CloseOutlined,
  Edit,
  InfoOutlined,
  KeyboardArrowDown,
  Link,
  LinkOffOutlined,
  RestartAlt,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
  Select,
  MenuItem,
  Stepper, Step, StepLabel,

} from "@mui/material";
import { styled } from "@mui/material/styles";
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { MdArrowBack } from "react-icons/md";
import CustomCheckBox from "src/components/customCheckBox";
import { API_SERVICE_BACKEND } from "src/config";
import RampupEdit from "./rampupEdit";
import { LoadingButton } from "@mui/lab";
import { CustomSwitch } from "src/components/custom-switch";

// Helper functions to convert between plain text and HTML for signatures
const htmlToPlainText = (html) => {
  if (!html) return "";
  // Remove HTML tags and convert <br/> to newlines
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
};

const plainTextToHtml = (text) => {
  if (!text) return "";
  // Convert newlines to <br/> and wrap in <p> tags
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== "");
  if (lines.length === 0) return "";
  return `<p>${lines.join("<br/>")}</p>`;
};

const RAMP_EPS = 1e-9;

const DEFAULT_RAMP_START = 1;
const DEFAULT_RAMP_INCREMENT = 0.5;

const hashRampSeed = (parts) => {
  const str = parts.map((p) => String(p)).join("|");
  let h = 1779033703;
  for (let i = 0; i < str.length; i += 1) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h >>> 0;
};

const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const roundToIncrement = (x, inc) => {
  if (!Number.isFinite(x) || !Number.isFinite(inc) || inc <= 0) return x;
  return Math.round(x / inc) * inc;
};

const isNonInteger = (v) => Math.abs(v - Math.round(v)) > 1e-6;

const integerizeRandomHalves = (arr, s, e, seedKey) => {
  const rng = mulberry32(hashRampSeed([s, e, seedKey, "intRound"]));
  const w = arr.map((x) => Number(Number(x).toFixed(4)));
  const n = w.length;
  if (n === 0) return w;
  const sInt = Math.round(s);
  const eInt = Math.round(e);
  w[0] = sInt;
  w[n - 1] = eInt;
  for (let i = 1; i < n - 1; i += 1) {
    let v = w[i];
    if (!isNonInteger(v)) {
      w[i] = Math.round(v);
      continue;
    }
    const lo = Math.floor(v);
    const hi = Math.ceil(v);
    w[i] = rng() < 0.5 ? lo : hi;
  }
  w[0] = sInt;
  w[n - 1] = eInt;
  for (let i = 0; i < n; i += 1) {
    w[i] = Math.max(sInt, Math.min(eInt, Math.round(Number(w[i]))));
  }
  return w;
};

const buildHumanLikeRampSchedule = (start, end, increment, seedKey) => {
  const s = Math.round(Number(start));
  const e = Math.round(Number(end));
  const inc = Number(increment);
  if (![s, e, inc].every(Number.isFinite) || inc <= 0 || e < s) return [];
  const n = e - s + 1;
  if (n === 1) return [Math.round(e)];

  const rng = mulberry32(hashRampSeed([s, e, inc, seedKey]));
  const out = new Array(n);
  out[0] = s;
  out[n - 1] = e;

  // Random walk relative to the previous day's value: most steps trend
  // upward but real dips (−1, −2) are common day-to-day. Any value may
  // appear at most twice in the whole schedule (consecutive doubles
  // allowed once); interior days are capped at `e - 1` so the final `e`
  // stays unique.
  const usedCount = new Map();
  usedCount.set(s, 1);
  const interiorMax = e - 1 >= s ? e - 1 : s;
  const isFull = (val) => (usedCount.get(val) || 0) >= 2;
  const MAX_UP = 4;
  const MAX_DOWN = 2;
  for (let i = 1; i < n - 1; i += 1) {
    const stepsRemaining = n - 1 - i;
    const r = rng();
    let step;
    if (r < 0.08) step = -2;
    else if (r < 0.22) step = -1;
    else if (r < 0.28) step = 0;
    else if (r < 0.52) step = 1;
    else if (r < 0.74) step = 2;
    else if (r < 0.90) step = 3;
    else step = 4;

    let v = out[i - 1] + step;
    const minReachable = e - MAX_UP * stepsRemaining;
    const maxReachable = e + MAX_DOWN * stepsRemaining;
    if (v < minReachable) v = minReachable;
    if (v > maxReachable) v = maxReachable;
    v = Math.max(s, Math.min(interiorMax, Math.round(v)));

    if (isFull(v)) {
      let up = v + 1;
      while (up <= interiorMax && isFull(up)) up += 1;
      let down = v - 1;
      while (down >= s && isFull(down)) down -= 1;
      if (up <= interiorMax) v = up;
      else if (down >= s) v = down;
    }

    out[i] = v;
    usedCount.set(v, (usedCount.get(v) || 0) + 1);
  }

  return out;
};

const DAILY_RAMP_SCHEDULE = [
  1, 2, 3, 4, 5,
  5, 5, 6, 7, 8,
  8, 8, 9, 10, 11,
  11, 11, 12, 13, 14,
  14, 14, 15, 16, 17,
  17, 17, 18, 19, 20,
];

// Generates a daily ramp schedule from start → end.
// Pattern: single day at start, then groups of +3 (hold 2 days) until end is reached.
const buildDailyRampScheduleForEnd = (end, start = 1) => {
  const e = Math.round(Number(end));
  const s = Math.round(Number(start));
  if (!Number.isFinite(e) || e < 1) return [s > 0 ? s : 1];
  if (s >= e) return [s];

  const schedule = [s];
  let current = s;

  while (current < e) {
    const burst = Math.min(3, e - current);
    for (let i = 0; i < burst; i++) {
      current++;
      schedule.push(current);
    }
    if (current < e) {
      schedule.push(current);
      schedule.push(current);
    }
  }

  return schedule;
};

const buildRampSchedule = (start, end, increment, seedKey = "") =>
  buildDailyRampScheduleForEnd(end, start);

const scheduleContainsValue = (scheduleList, value) =>
  scheduleList.some((v) => Math.abs(Number(v) - Number(value)) < RAMP_EPS);

const OLD_MAILBOX_START = 1;
const OLD_MAILBOX_END = 20;
const OLD_MAILBOX_INCREMENT = 0.5;

const YOUNG_MAILBOX_START = 1;
const YOUNG_MAILBOX_END = 13;
const YOUNG_MAILBOX_INCREMENT = 0.5;

const deriveRampEndCap = (acc, scheduleFallback = []) => {
  const ma = Number(acc?.maxAllowed);
  if (Number.isFinite(ma) && ma >= 1) return Math.round(ma);
  const last = Number(scheduleFallback[scheduleFallback.length - 1]);
  if (Number.isFinite(last) && last >= 1) return Math.round(last);
  return OLD_MAILBOX_END;
};

const MAX_SENDING_VARIANCE_PCT = 50;
const DEFAULT_SENDING_VARIANCE_PCT = 30;
const VARIANCE_OPTIONS = [0, 10, 20, 30, 40, 50];

const clampSendingVariancePct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || Number.isNaN(n)) return DEFAULT_SENDING_VARIANCE_PCT;
  return Math.min(MAX_SENDING_VARIANCE_PCT, Math.max(0, Math.round(n)));
};

/** Empty input on save/cancel commit → default 30. */
const normalizeVarianceForSave = (v) => {
  if (v === "") return DEFAULT_SENDING_VARIANCE_PCT;
  return clampSendingVariancePct(v);
};

const ConfigUpdate = ({
  open,
  handleClose,
  acc,
  fetchAllMailBoxes,
  userEmail,
  isBulkUpdate = false,
  selectedAccounts,
  setSelectedAccounts,
  userType
}) => {
  const theme = useTheme();

  const rampScheduleSeed = useMemo(
    () =>
      isBulkUpdate
        ? selectedAccounts?.length
          ? [...selectedAccounts].map(String).sort().join(",")
          : "bulk"
        : String(acc?._id ?? ""),
    [isBulkUpdate, selectedAccounts, acc?._id]
  );

  const defaultHourlyLimit = acc?.hourlyLimit || 0;
  const upcomingSchedule = acc?.rampUp?.schedule || [];
  const checkSchedule = useMemo(
    () =>
      buildRampSchedule(
        YOUNG_MAILBOX_START,
        YOUNG_MAILBOX_END,
        YOUNG_MAILBOX_INCREMENT,
        rampScheduleSeed
      ),
    [rampScheduleSeed]
  );
  const defaultMode = acc?.rampUp?.type || "DEFAULT";
  const finalSchedule = acc?.rampUp?.schedule?.length > 0
    ? [...acc.rampUp.schedule]
    : [...DAILY_RAMP_SCHEDULE];

  const validDailyLimit = finalSchedule?.findIndex(sch => Number(sch) === Number(acc?.maxAllowed)) !== -1;
  // const defaultDailyLimit = validDailyLimit ? acc?.maxAllowed : 0;
  const defaultDailyLimit = validDailyLimit
    ? acc?.maxAllowed
    : finalSchedule[0] ?? 0;
  const defaultInWeek = acc?.inWeek || 0;
  const [hourlyLimit, setHourlyLimit] = useState(defaultHourlyLimit);
  const [dailyLimit, setDailyLimit] = useState(defaultDailyLimit);
  const [dailyLimitInWeek, setDailyLimitInWeek] = useState(defaultInWeek - 1);
  const [schedule, setSchedule] = useState(finalSchedule);
  const [mode, setMode] = useState(defaultMode);

  const [isEditing, setIsEditing] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);


  const [startValue, setStartValue] = useState(finalSchedule[0] ?? "");
  const [endValue, setEndValue] = useState(schedule[schedule.length - 1] ?? "");
  const [endRawInput, setEndRawInput] = useState(String(schedule[schedule.length - 1] ?? OLD_MAILBOX_END));
  const [increaseValue, setIncreaseValue] = useState(DEFAULT_RAMP_INCREMENT);

  const [fixedDailySend, setFixedDailySend] = useState("");
  const [showRampPreview, setShowRampPreview] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isEditingRamp, setIsEditingRamp] = useState(false);
  const [originalReplyTo, setOriginalReplyTo] = useState("");
  const [originalSignature, setOriginalSignature] = useState("");
  const [step1Completed, setStep1Completed] = useState(false);
  const [committedSchedule, setCommittedSchedule] = useState(finalSchedule);
  const [committedDailyLimit, setCommittedDailyLimit] = useState(defaultDailyLimit);
  /** 0-based ramp step; source of truth for which tile is “current” when caps repeat (e.g. day1=1, day2=1). */
  const [committedDailyLimitInWeek, setCommittedDailyLimitInWeek] = useState(
    defaultInWeek - 1
  );
  const [sendingVariancePercentage, setSendingVariancePercentage] = useState(() =>
    clampSendingVariancePct(acc?.sendingVariancePercentage)
  );
  const [committedSendingVariancePercentage, setCommittedSendingVariancePercentage] =
    useState(() => clampSendingVariancePct(acc?.sendingVariancePercentage));
  const [isSuspending, setIsSuspending] = useState(false);
  const [suspended, setSuspended] = useState({
    value: acc?.isSuspended?.value || false,
    reason: acc?.isSuspended?.reason || ""
  });
  const [isResetting, setIsResetting] = useState(false);

  /** idle | loading | young | old — from GET /checkmailboxage/:mailboxId */
  const [mailboxAgeStatus, setMailboxAgeStatus] = useState("idle");
  const isRampLockedByAge =
    mailboxAgeStatus === "old" && !isBulkUpdate;

  // config

  const [config, setConfig] = useState({
    fullName: acc?.fullName || "",
    replyTo: acc?.msgConf?.replyTo || "",
    signature: htmlToPlainText(acc?.msgConf?.signature || ""), // Store as plain text for editing
    // isSuspended: acc?.isSuspended || 
    // {
    //   value: false,
    //   reason: "",
    //   until: "",
    //   data: {},
    // },
  });

  const handleChange = (key, value) => {
    if (key === "isSuspended") {
      setConfig((prev) => ({ ...prev, [key]: { ...prev?.isSuspended, value } }));
    } else {
      setConfig((prev) => ({ ...prev, [key]: value }));
    }
  };
  const handleSuspendToggle = async () => {
    try {
      setIsSuspending(true)
      let payload = {
        isSuspended: !suspended?.value,
      };
      const res = await axios.put(
        `${API_SERVICE_BACKEND}/mailbox/change/status/${acc?._id}`,
        payload
      );
      setSuspended(prev => {
        let updatedValue = !prev?.value;
        let updatedReason = updatedValue ? "BY_CLIENT" : "";
        return {
          value: updatedValue,
          reason: updatedReason
        }
      });
      setIsSuspending(false);
      fetchAllMailBoxes(userEmail, true, setLoading);
      toast.success(`Email account ${suspended?.value ? "unsuspended" : "suspended"} successfully !`)
    } catch (err) {
      //console.log(err);
      setIsSuspending(false);
      toast.error(`Failed to ${suspended?.value ? "unsuspend" : "suspend"} email account!`)
    }
  };

  useEffect(() => {
    if (!open) return;

    if (isBulkUpdate && !acc) {
      const built = buildRampSchedule(
        DEFAULT_RAMP_START,
        OLD_MAILBOX_END,
        DEFAULT_RAMP_INCREMENT,
        rampScheduleSeed
      );
      setHourlyLimit(0);
      setCommittedSchedule(built);
      setCommittedDailyLimit(built[0] ?? DEFAULT_RAMP_START);
      setCommittedDailyLimitInWeek(0);
      setSchedule(built);
      setDailyLimit(built[0] ?? DEFAULT_RAMP_START);
      setDailyLimitInWeek(0);
      setStartValue(DEFAULT_RAMP_START);
      setEndValue(OLD_MAILBOX_END);
      setIncreaseValue(DEFAULT_RAMP_INCREMENT);
      setSendingVariancePercentage(DEFAULT_SENDING_VARIANCE_PCT);
      setCommittedSendingVariancePercentage(DEFAULT_SENDING_VARIANCE_PCT);
      setMode("CUSTOM");
      return;
    }

    if (!acc) return;

    const defaultHourlyLimit = acc?.hourlyLimit || 0;
    const upcomingSchedule = acc?.rampUp?.schedule || [];
    const defaultMode = acc?.rampUp?.type || "DEFAULT";
    const savedSch = acc?.rampUp?.schedule;
    const finalSchedule = savedSch?.length > 0
      ? [...savedSch]
      : [...DAILY_RAMP_SCHEDULE];

    const defaultInWeek = acc?.inWeek || 0;
    const variancePct = clampSendingVariancePct(acc?.sendingVariancePercentage);

    setHourlyLimit(defaultHourlyLimit);
    setConfig({
      fullName: acc?.fullName || "",
      replyTo: acc?.msgConf?.replyTo || "",
      signature: htmlToPlainText(acc?.msgConf?.signature || ""),
    });
    // 90+ day mailboxes: same ramp everywhere (view + edit + save) — not server schedule
    if (mailboxAgeStatus === "old" && !isBulkUpdate) {
      const built = buildRampSchedule(
        OLD_MAILBOX_START,
        OLD_MAILBOX_END,
        OLD_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        OLD_MAILBOX_START;
      const dlIdx = built.findIndex((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS);
      setCommittedSchedule(built);
      setCommittedDailyLimit(dl);
      const idx = dlIdx >= 0 ? dlIdx : 0;
      setCommittedDailyLimitInWeek(idx);
      setSchedule(built);
      setDailyLimit(dl);
      setDailyLimitInWeek(idx);
      setMode("CUSTOM");
      setSendingVariancePercentage(variancePct);
      setCommittedSendingVariancePercentage(variancePct);
      return;
    }

    // <90 day mailboxes: always rebuild a fresh 13-day randomized ramp (ignore saved schedule)
    if (mailboxAgeStatus === "young" && !isBulkUpdate) {
      const built = buildRampSchedule(
        YOUNG_MAILBOX_START,
        YOUNG_MAILBOX_END,
        YOUNG_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        YOUNG_MAILBOX_START;
      const dlIdx = built.findIndex((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS);
      setCommittedSchedule(built);
      setCommittedDailyLimit(dl);
      const idx = dlIdx >= 0 ? dlIdx : 0;
      setCommittedDailyLimitInWeek(idx);
      setSchedule(built);
      setDailyLimit(dl);
      setDailyLimitInWeek(idx);
      setMode("CUSTOM");
      setSendingVariancePercentage(variancePct);
      setCommittedSendingVariancePercentage(variancePct);
      return;
    }

    const validDl =
      finalSchedule?.some(
        (sch) => Math.abs(Number(sch) - Number(acc?.maxAllowed)) < RAMP_EPS
      ) ?? false;
    const defaultDailyLimit = validDl ? acc?.maxAllowed : finalSchedule[0] ?? 0;

    let adjustedDailyLimit = defaultDailyLimit;
    if (
      defaultDailyLimit !== null &&
      !finalSchedule.includes(defaultDailyLimit)
    ) {
      const numericCheckSchedule = finalSchedule
        .map((v) => parseInt(v, 10))
        .filter((v) => !isNaN(v));

      if (numericCheckSchedule.length > 0) {
        adjustedDailyLimit = numericCheckSchedule.reduce((prev, curr) =>
          Math.abs(curr - defaultDailyLimit) <
            Math.abs(prev - defaultDailyLimit)
            ? curr
            : prev
        );
      }
    }
    const savedEnd = Number(finalSchedule[finalSchedule.length - 1]);
    const savedStart = Number(finalSchedule[0]);

    setCommittedSchedule(finalSchedule);
    setCommittedDailyLimit(defaultDailyLimit);
    setCommittedDailyLimitInWeek(defaultInWeek - 1);

    setDailyLimit(defaultDailyLimit);
    setDailyLimitInWeek(defaultInWeek - 1);
    setSchedule(finalSchedule);
    setSendingVariancePercentage(variancePct);
    setCommittedSendingVariancePercentage(variancePct);
    if (Number.isFinite(savedEnd)) setEndValue(savedEnd);
    if (Number.isFinite(savedStart) && savedStart >= 1) {
      setStartValue(Math.round(savedStart));
    }
  }, [acc, open, mailboxAgeStatus, isBulkUpdate, rampScheduleSeed]);


  const cancelAndReset = () => {
    if (mailboxAgeStatus === "old" && !isBulkUpdate && acc) {
      const built = buildRampSchedule(
        OLD_MAILBOX_START,
        OLD_MAILBOX_END,
        OLD_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        OLD_MAILBOX_START;
      const dlIdx = built.findIndex((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS);
      const idx = dlIdx >= 0 ? dlIdx : 0;
      setSchedule(built);
      setCommittedSchedule(built);
      setCommittedDailyLimit(dl);
      setCommittedDailyLimitInWeek(idx);
      setDailyLimit(dl);
      setDailyLimitInWeek(idx);
      setMode("CUSTOM");
      return;
    }
    if (mailboxAgeStatus === "young" && !isBulkUpdate && acc) {
      const built = buildRampSchedule(
        YOUNG_MAILBOX_START,
        YOUNG_MAILBOX_END,
        YOUNG_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        YOUNG_MAILBOX_START;
      const dlIdx = built.findIndex((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS);
      const idx = dlIdx >= 0 ? dlIdx : 0;
      setSchedule(built);
      setCommittedSchedule(built);
      setCommittedDailyLimit(dl);
      setCommittedDailyLimitInWeek(idx);
      setDailyLimit(dl);
      setDailyLimitInWeek(idx);
      setMode("CUSTOM");
      return;
    }
    const restoreSched =
      committedSchedule?.length > 0 ? committedSchedule : finalSchedule;
    setSchedule(restoreSched.map((x) => x));
    const restoreDl = scheduleContainsValue(restoreSched, committedDailyLimit)
      ? committedDailyLimit
      : restoreSched[0] ?? "";
    setDailyLimit(restoreDl);
    const ciw = Math.min(
      Math.max(0, committedDailyLimitInWeek),
      Math.max(0, restoreSched.length - 1)
    );
    setDailyLimitInWeek(ciw);
    setMode(defaultMode);
    setSendingVariancePercentage(committedSendingVariancePercentage);
  };

  // Exit edit mode on blur

  // const reset = () => {
  //   setSchedule(checkSchedule);
  //   setIsWeekly(true);
  //   setMode("DEFAULT");


  //   if (dailyLimit !== null && !checkSchedule.includes(dailyLimit.toString())) {
  //     const numericCheckSchedule = checkSchedule
  //       .map((v) => parseInt(v))
  //       .filter((v) => !isNaN(v));

  //     if (numericCheckSchedule.length > 0) {
  //       const closestValue = numericCheckSchedule.reduce((prev, curr) =>
  //         Math.abs(curr - dailyLimit) < Math.abs(prev - dailyLimit)
  //           ? curr
  //           : prev
  //       );

  //       setDailyLimit(closestValue);
  //     }
  //   }
  // };
  useEffect(() => {
    if (acc) {
      setConfig({
        fullName: acc.fullName || "",
        replyTo: acc.msgConf?.replyTo || "",
        signature: htmlToPlainText(acc.msgConf?.signature || ""), // Convert HTML to plain text for editing
      });

      setOriginalReplyTo(acc.msgConf?.replyTo || "");
      setOriginalSignature(acc.msgConf?.signature || ""); // Keep original HTML for comparison
    }
  }, [acc]);


  const reset = () => {
    if (mailboxAgeStatus === "old" && !isBulkUpdate) {
      setIsResetting(true);
      const built = buildRampSchedule(
        OLD_MAILBOX_START,
        OLD_MAILBOX_END,
        OLD_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      setSchedule(built);
      setMode("CUSTOM");
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        OLD_MAILBOX_START;
      setDailyLimit(dl);
      const dlIdx = built.findIndex((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS);
      setDailyLimitInWeek(dlIdx >= 0 ? dlIdx : 0);
      setStartValue(OLD_MAILBOX_START);
      setEndValue(OLD_MAILBOX_END);
      setIncreaseValue(OLD_MAILBOX_INCREMENT);
      setSendingVariancePercentage(
        clampSendingVariancePct(acc?.sendingVariancePercentage)
      );
      setIsResetting(false);
      return;
    }

    if (mailboxAgeStatus === "young" && !isBulkUpdate) {
      setIsResetting(true);
      const built = buildRampSchedule(
        YOUNG_MAILBOX_START,
        YOUNG_MAILBOX_END,
        YOUNG_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      setSchedule(built);
      setMode("CUSTOM");
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        YOUNG_MAILBOX_START;
      setDailyLimit(dl);
      const dlIdx = built.findIndex((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS);
      setDailyLimitInWeek(dlIdx >= 0 ? dlIdx : 0);
      setStartValue(YOUNG_MAILBOX_START);
      setEndValue(OLD_MAILBOX_END);
      setIncreaseValue(YOUNG_MAILBOX_INCREMENT);
      setSendingVariancePercentage(
        clampSendingVariancePct(acc?.sendingVariancePercentage)
      );
      setIsResetting(false);
      return;
    }

    const upcomingSchedule = acc?.rampUp?.schedule || [];
    const defaultMode = acc?.rampUp?.type || "DEFAULT";
    const finalSchedule =
      defaultMode === "DEFAULT" ? checkSchedule : upcomingSchedule;
    const defaultInWeek = acc?.inWeek || 0;
    // Same logic as initial setup
    // const validDailyLimit = finalSchedule?.findIndex(
    //   sch => Number(sch) === Number(acc?.maxAllowed)
    // ) !== -1;

    // const defaultDailyLimit = validDailyLimit
    //   ? acc?.maxAllowed
    //   : finalSchedule[0] ?? 0;
    const endCap = deriveRampEndCap(acc, finalSchedule);
    const built = buildRampSchedule(
      DEFAULT_RAMP_START,
      endCap,
      DEFAULT_RAMP_INCREMENT,
      rampScheduleSeed
    );
    const dl =
      built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
      built[built.length - 1] ??
      built[0] ??
      DEFAULT_RAMP_START;
    const dlIdx = built.findIndex((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS);

    setIsResetting(true);
    setSchedule(built);
    setMode(defaultMode);
    setDailyLimit(dl);
    setDailyLimitInWeek(dlIdx >= 0 ? dlIdx : 0);

    setStartValue(DEFAULT_RAMP_START);
    setEndValue(OLD_MAILBOX_END);
    setIncreaseValue(DEFAULT_RAMP_INCREMENT);
    setSendingVariancePercentage(
      clampSendingVariancePct(acc?.sendingVariancePercentage)
    );
    setIsResetting(false);
  };


  useEffect(() => {
    if (!open) return;
    const totalItems = schedule?.length ?? 0;
    if (!totalItems) {
      setStartIndex(0);
      return;
    }
    const selectedIndex = Math.min(
      Math.max(0, dailyLimitInWeek),
      totalItems - 1
    );

    let newStartIndex = 0;
    if (totalItems <= 6) {
      newStartIndex = 0;
    } else if (selectedIndex >= totalItems - (totalItems % 6)) {
      newStartIndex = Math.max(0, totalItems - 6);
    } else {
      newStartIndex = Math.floor(selectedIndex / 6) * 6;
    }
    setStartIndex(newStartIndex);
  }, [open, schedule, dailyLimitInWeek]);

  const handleSaveConfig = async () => {
    const len = schedule?.length ?? 0;
    const idx =
      len > 0 &&
      Number.isFinite(dailyLimitInWeek) &&
      dailyLimitInWeek >= 0 &&
      dailyLimitInWeek < len
        ? dailyLimitInWeek
        : schedule?.findIndex(
            (v) => Math.abs(Number(v) - Number(dailyLimit)) < RAMP_EPS
          ) ?? -1;
    const updatedInWeek = idx !== -1 ? idx + 1 : 1;
    try {
      let rampupPayload;
      let url;
      let payload;

      rampupPayload = {
        maxAllowed: Math.round(Number(dailyLimit)) || 0,
        inWeek: updatedInWeek,
        sendingVariancePercentage: normalizeVarianceForSave(sendingVariancePercentage),
        rampUp: {
          schedule: schedule,
          frequency: "DAILY",
          type: mode,
        },
      };

      // setIsEditing(false);
      setLoading(true);
      if (isBulkUpdate) {
        rampupPayload.mailboxes = selectedAccounts;
        url = `${API_SERVICE_BACKEND}/mailboxes/change/status`;
        payload = rampupPayload;
        const res = await axios.put(url, payload);
        setSelectedAccounts([]);
        reset()
      } else {
        // rampupPayload.hourlyLimit = hourlyLimit || 0;
        url = `${API_SERVICE_BACKEND}/mailbox/${acc?._id}`;
        payload = {
          set: {
            // fullName: config?.fullName,
            //isSuspended: config?.isSuspended,

            // msgConf: {
            //   replyTo: config?.replyTo,
            // },
            ...rampupPayload,
          },
        };
        const res = await axios.patch(url, payload);
      }
      setLoading(false);
      setIsEditing(false);
      handleClose();
      toast.success("Successfully updated!");
      setCommittedSchedule([...schedule]);
      setCommittedDailyLimit(dailyLimit);
      setCommittedDailyLimitInWeek(idx >= 0 ? idx : 0);
      setCommittedSendingVariancePercentage(
        normalizeVarianceForSave(sendingVariancePercentage)
      );

      fetchAllMailBoxes(userEmail, true, setLoading);
    } catch (err) {
      toast.error(err?.response?.data?.message);
      //console.log("err", err);
    }
  };

  const handleReplyToUpdate = async () => {
    if (config?.replyTo === originalReplyTo) return;

    try {
      setReplyLoading(true);
      const url = `${API_SERVICE_BACKEND}/mailbox/${acc?._id}`;
      const payload = {
        set: {
          msgConf: {
            replyTo: config.replyTo,
          },
        },
      };
      await axios.patch(url, payload);
      setReplyLoading(false);
      toast.success("Reply To updated successfully!");
      setOriginalReplyTo(config.replyTo);
      fetchAllMailBoxes(userEmail, true, setLoading);
    } catch (err) {
      setReplyLoading(false);
      toast.error(err?.response?.data?.message || "Failed to update Reply To");
      //console.log("Reply To update error", err);
    }
  };

  const handleSignatureUpdate = async () => {
    // Convert plain text to HTML before comparing
    const signatureHtml = plainTextToHtml(config.signature || "");
    if (signatureHtml === originalSignature) return;

    try {
      setSignatureLoading(true);
      const url = `${API_SERVICE_BACKEND}/mailbox/${acc?._id}`;
      const payload = {
        set: {
          msgConf: {
            signature: signatureHtml, // Convert plain text to HTML before sending
          },
        },
      };
      const response = await axios.patch(url, payload);
      setSignatureLoading(false);
      
      // Check if update was successful
      if (response.data?.status === true) {
        toast.success("Signature updated successfully!");
        setOriginalSignature(signatureHtml); // Store HTML version
        fetchAllMailBoxes(userEmail, true, setLoading);
      } else {
        // Update failed
        toast.error("Signature was not updated");
      }
    } catch (err) {
      setSignatureLoading(false);
      toast.error("Failed to update Signature");
      //console.log("Signature update error", err);
    }
  };

  const handleConfigClose = () => {
    setIsEditing(false);
    handleClose();
    cancelAndReset();
  };
  const handleCancel = () => {
    setSchedule(committedSchedule);
    const restoreDl = scheduleContainsValue(committedSchedule, committedDailyLimit)
      ? committedDailyLimit
      : committedSchedule[0] ?? "";
    setDailyLimit(restoreDl);
    if (isRampLockedByAge) {
      setStartValue(OLD_MAILBOX_START);
      setEndValue(OLD_MAILBOX_END);
      setIncreaseValue(OLD_MAILBOX_INCREMENT);
    } else {
      setStartValue(committedDailyLimit ?? committedSchedule[0] ?? "");
      setEndValue(committedSchedule[committedSchedule.length - 1] ?? "");
      setIncreaseValue(
        committedSchedule.length >= 2
          ? committedSchedule[1] - committedSchedule[0]
          : DEFAULT_RAMP_INCREMENT
      );
    }
    setIsEditing(false);
    setStep1Completed(false);
    setCurrentStep(1);
    setDailyLimitInWeek(committedDailyLimitInWeek);
    setSendingVariancePercentage(committedSendingVariancePercentage);
  };


  /** One source of truth: same length as `rampUp.schedule` / preview tiles (×7 calendar days if weekly). */
  const rampEndDatecal = useMemo(() => {
    if (!schedule?.length) return null;
    const totalDays = schedule.length;
    const endDate = new Date();
    endDate.setUTCDate(endDate.getUTCDate() + totalDays);
    return endDate;
  }, [schedule]);

  const rampStartEndEqual =
    startValue !== "" &&
    endValue !== "" &&
    !Number.isNaN(Number(startValue)) &&
    !Number.isNaN(Number(endValue)) &&
    Number(startValue) === Number(endValue);

  const formatRampEndDate = (date) => {
    if (!date) return "--";

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const day = date.getUTCDate();
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();


    return `${month} ${day}, ${year}`;
  };


  const generateSchedule = () => {
    if (
      startValue === "" ||
      endValue === "" ||
      increaseValue === "" ||
      Number(increaseValue) <= 0
    ) {
      setSchedule([]);
      return;
    }

    const scheduleList = buildRampSchedule(
      startValue,
      endValue,
      increaseValue,
      rampScheduleSeed
    );
    setSchedule(scheduleList);
  };

  useEffect(() => {
    // View mode: schedule comes from hydration / committed state only.
    // Without this guard, cancel (or any path that sets increaseValue to "")
    // re-runs this effect and clears schedule with setSchedule([]).
    if (!isEditing) return;

    if (
      startValue === "" ||
      endValue === "" ||
      increaseValue === "" ||
      Number(increaseValue) <= 0
    ) {
      setSchedule([]);
      setDailyLimit("");
      return;
    }

    const scheduleList = buildRampSchedule(
      startValue,
      endValue,
      increaseValue,
      rampScheduleSeed
    );
    setSchedule(scheduleList);
    if (!scheduleList.length) {
      setDailyLimit("");
      setDailyLimitInWeek(0);
    } else {
      const clamped = Math.min(
        Math.max(0, dailyLimitInWeek),
        scheduleList.length - 1
      );
      setDailyLimitInWeek(clamped);
      setDailyLimit(scheduleList[clamped]);
    }

  }, [isEditing, startValue, endValue, increaseValue, rampScheduleSeed]);

  useEffect(() => {
    if (!isEditing) return;

    if (mailboxAgeStatus === "old" && !isBulkUpdate) {
      setStartValue(OLD_MAILBOX_START);
      setEndValue(OLD_MAILBOX_END);
      setIncreaseValue(OLD_MAILBOX_INCREMENT);
      const built = buildRampSchedule(
        OLD_MAILBOX_START,
        OLD_MAILBOX_END,
        OLD_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        "";
      setSchedule(built);
      const dlIdx2 = built.findIndex(
        (v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS
      );
      setDailyLimit(dl);
      setDailyLimitInWeek(dlIdx2 >= 0 ? dlIdx2 : 0);
      return;
    }

    if (mailboxAgeStatus === "young" && !isBulkUpdate) {
      setStartValue(YOUNG_MAILBOX_START);
      setEndValue(OLD_MAILBOX_END);
      setIncreaseValue(YOUNG_MAILBOX_INCREMENT);
      const built = buildRampSchedule(
        YOUNG_MAILBOX_START,
        YOUNG_MAILBOX_END,
        YOUNG_MAILBOX_INCREMENT,
        rampScheduleSeed
      );
      const dl =
        built.find((v) => Math.abs(Number(v) - Number(acc?.maxAllowed)) < RAMP_EPS) ??
        built[0] ??
        "";
      setSchedule(built);
      const dlIdx2 = built.findIndex(
        (v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS
      );
      setDailyLimit(dl);
      setDailyLimitInWeek(dlIdx2 >= 0 ? dlIdx2 : 0);
      return;
    }

    const endCap = deriveRampEndCap(acc, committedSchedule);
    const savedScheduleStart = !isBulkUpdate && acc?.rampUp?.schedule?.length > 0
      ? Number(acc.rampUp.schedule[0])
      : NaN;
    const startAt = Number.isFinite(savedScheduleStart) && savedScheduleStart >= 1 && savedScheduleStart <= 50
      ? Math.round(savedScheduleStart)
      : DEFAULT_RAMP_START;

    const pickLimitFromBuilt = (built) => {
      const target = Number(acc?.maxAllowed);
      if (Number.isFinite(target)) {
        const byMax = built.findIndex(
          (v) => Math.abs(Number(v) - target) < RAMP_EPS
        );
        if (byMax >= 0) return { dl: built[byMax], idx: byMax };
      }
      if (scheduleContainsValue(built, committedDailyLimit)) {
        const idx = built.findIndex(
          (v) => Math.abs(Number(v) - Number(committedDailyLimit)) < RAMP_EPS
        );
        if (idx >= 0) return { dl: built[idx], idx };
      }
      const last = built.length - 1;
      return { dl: built[last], idx: last };
    };

    const savedScheduleEnd = !isBulkUpdate && acc?.rampUp?.schedule?.length > 0
      ? Number(acc.rampUp.schedule[acc.rampUp.schedule.length - 1])
      : NaN;
    const resolvedEnd = Number.isFinite(savedScheduleEnd) && savedScheduleEnd >= 2 && savedScheduleEnd <= 50
      ? Math.round(savedScheduleEnd)
      : OLD_MAILBOX_END;

    setStartValue(startAt);
    setEndValue(resolvedEnd);
    setIncreaseValue(DEFAULT_RAMP_INCREMENT);
    const built = buildRampSchedule(
      startAt,
      resolvedEnd,
      DEFAULT_RAMP_INCREMENT,
      rampScheduleSeed
    );
    const inWeekIdx =
      !isBulkUpdate && Number.isFinite(defaultInWeek) && defaultInWeek >= 1 && (defaultInWeek - 1) < built.length
        ? defaultInWeek - 1
        : Math.max(0, built.length - 1);
    setSchedule(built);
    setDailyLimit(built[inWeekIdx]);
    setDailyLimitInWeek(inWeekIdx);
  }, [
    isEditing,
    committedSchedule,
    mailboxAgeStatus,
    isBulkUpdate,
    acc?.maxAllowed,
    committedDailyLimit,
    rampScheduleSeed,
  ]);

  // reset to Step 1
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setShowRampPreview(false);
    }
  }, [open]);

  // Keep raw input display in sync when endValue is set programmatically
  useEffect(() => {
    setEndRawInput(endValue === "" ? "" : String(endValue));
  }, [endValue]);



  // const steps = [
  //   {
  //     label: "Enter ramp values",
  //     completed:
  //       startValue !== "" &&
  //       endValue !== "" &&
  //       increaseValue !== "" &&
  //       dailyLimit !== "" &&
  //       typeof isWeekly === "boolean",
  //   },
  //   {
  //     label: "See preview",
  //     completed: currentStep >= 2,
  //   },
  // ];
  const CustomStepIcon = (props) => {
    const { active, completed, icon } = props;

    return completed ? (
      <CheckCircleIcon sx={{ color: theme => theme.palette.success.main }} />
    ) : (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          backgroundColor: (theme) =>
            active
              ? theme.palette.primary.main
              : theme.palette.mode === "dark"
                ? "#898888ff"
                : "#e0e0e0",
        }}
      >
        <Typography variant="caption" sx={{
          color: active ? "#ffffff" : "text.secondary",
          fontWeight: 600,
        }}
        >
          {icon}
        </Typography>
      </Box>
    );
  };
  const steps = [
    {
      label: "Enter ramp values",
      completed: step1Completed,
    },
    {
      label: "See preview",
      // completed: currentStep >= 2,
      completed: false,
    },
  ];

  const getTooltip = (reason) => {
    switch (reason) {
      case "BY_ADMIN": return "Mailbox suspended by the CSM. Please contact the support team."
      case "WARMING_UP": return "Mailbox is warming up."
      case "BY_CLIENT": return "Mailbox suspended by the user. Click to unsuspend";
      default: return "Unknown reason Suspended"
    }
  }
  const tooltipMessage = suspended?.value ? getTooltip(suspended?.reason) : "Click to suspend"

  useEffect(() => {
    if (!open) return;
    if (!isEditing && !isBulkUpdate) return;
    const target = buildDailyRampScheduleForEnd(endValue, startValue);
    const sameAsTarget = (arr) =>
      Array.isArray(arr) &&
      arr.length === target.length &&
      arr.every((v, i) => Math.abs(Number(v) - Number(target[i])) < RAMP_EPS);

    if (!sameAsTarget(schedule)) setSchedule([...target]);
    if (!isEditing && !sameAsTarget(committedSchedule)) setCommittedSchedule([...target]);

    const remap = (dl, prevIdx) => {
      const dlNum = Number(dl);
      // Prefer the existing index if value still matches (handles repeated values correctly)
      if (Number.isFinite(prevIdx) && prevIdx >= 0 && prevIdx < target.length &&
          Math.abs(Number(target[prevIdx]) - dlNum) < RAMP_EPS) {
        return prevIdx;
      }
      const byValue = target.findIndex((v) => Math.abs(Number(v) - dlNum) < RAMP_EPS);
      if (byValue >= 0) return byValue;
      return Math.min(Math.max(0, Number.isFinite(prevIdx) ? prevIdx : 0), target.length - 1);
    };
    setDailyLimitInWeek((idx) => remap(dailyLimit, idx));
    if (!isEditing) setCommittedDailyLimitInWeek((idx) => remap(committedDailyLimit, idx));
    setDailyLimit((dl) => (target.some((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS) ? dl : target[0]));
    if (!isEditing) setCommittedDailyLimit((dl) => (target.some((v) => Math.abs(Number(v) - Number(dl)) < RAMP_EPS) ? dl : target[0]));
  }, [isEditing, open, startValue, endValue, schedule, committedSchedule]);

  return (
    <>
      <Dialog open={open} onClose={handleConfigClose} sx={{ "& .MuiPaper-root": { maxWidth: { sm: "100%", md: "600px" } } }} fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 1,
            py: 1.5,
            ml: 1.2
          }}
        >

          <Box sx={{ flex: 1, maxWidth: "400px", }}>
            {(isBulkUpdate || isEditing) ? (

              <Stepper activeStep={currentStep - 1}>
                {steps.map((stepItem, index) => {
                  const isActive = currentStep - 1 === index;
                  const isCompleted = stepItem.completed;

                  return (
                    <Step key={stepItem.label} completed={isCompleted}>
                      <StepLabel
                        StepIconComponent={CustomStepIcon}
                        sx={{
                          ".MuiStepLabel-label": {
                            color: isCompleted
                              ? (theme) => `${theme.palette.success.main} !important`
                              : isActive
                                ? (theme) => `${theme.palette.primary.main} !important`
                                : (theme) => `${theme.palette.text.secondary} !important`,
                            fontWeight: isActive ? 600 : 500,
                          },
                        }}
                      >
                        {stepItem.label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

            ) : (
              <Stack flexDirection={"row"} justifyContent={"flex-start"} alignItems={"center"} gap={1}>
                <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                  Configure Account
                </Typography>
                {userType === "enterprise" && (
                  <Tooltip title={tooltipMessage}>
                    <span>
                      <CustomSwitch
                        checked={!suspended?.value}
                        onClick={handleSuspendToggle}
                        disabled={suspended?.value && suspended?.reason !== "BY_CLIENT"}
                      />
                    </span>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>


          <IconButton onClick={handleConfigClose}>
            <CloseOutlined sx={{ color: (theme) => theme.palette.text.primary }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: '8px', pb: '8px', pr: '20px', pl: '20px' }}>
          <Stack spacing={1}>
            {!isBulkUpdate && !isEditing && (
              <>
                <Stack spacing={1}>
                  <InputLabel>Name</InputLabel>
                  <TextField
                    variant="outlined"
                    className="glass-input"
                    value={config?.fullName}
                    onChange={(e) => handleChange("fullName", e.target.value)}
                    disabled
                  />
                </Stack>

                <Stack spacing={1}>
                  <InputLabel>Reply To</InputLabel>
                  <TextField
                    variant="outlined"
                    className="glass-input"
                    value={config?.replyTo}
                    onChange={(e) => handleChange("replyTo", e.target.value)}
                    sx={{ flex: 1 }}
                    InputProps={{
                      endAdornment: (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleReplyToUpdate}
                          disabled={
                            // !config?.replyTo ||
                            config?.replyTo === originalReplyTo ||
                            replyLoading
                          }
                        >
                          {replyLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            "Update"
                          )}
                        </Button>

                      ),
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <InputLabel>Signature</InputLabel>
                  <TextField
                    variant="outlined"
                    className="glass-input"
                    value={config?.signature}
                    onChange={(e) => handleChange("signature", e.target.value)}
                    placeholder="Best regards,&#10;Your Name&#10;CEO, Company Inc."
                    multiline
                    minRows={3}
                    sx={{ flex: 1 }}
                    InputProps={{
                      endAdornment: (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSignatureUpdate}
                          disabled={
                            plainTextToHtml(config?.signature || "") === originalSignature ||
                            signatureLoading
                          }
                        >
                          {signatureLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            "Update"
                          )}
                        </Button>
                      ),
                    }}
                  />
                  <Typography sx={{ fontSize: "12px", color: theme.palette.text.secondary }}>
                    This signature will be appended when sending from this mailbox. Enter plain text with line breaks.
                  </Typography>
                </Stack>

              </>
            )}
            {/* <Card
              sx={{
                p: 2,
                width: "100%" ,
              }}
              className="glass-wrapper-50"
            > */}
            {/* {isEditing ? (
                <>
                  <RampupEdit
                    setIsEditing={setIsEditing}
                    acc={acc}
                    initialValues={{
                      schedule,
                      dailyLimit,
                      isWeekly,
                      startIdx: startIndex,
                    }}
                    setSchedule={setSchedule}
                    setIsWeekly={setIsWeekly}
                    setDailyLimit={setDailyLimit}
                    setMode={setMode}
                  />
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Stack
                      flexDirection="row"
                      justifyContent="flex-start"
                      alignItems="center"
                      gap={1}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        Rampup Schedule
                      </Typography>
                      <>
                        {loading ? (
                          <CircularProgress size={15} sx={{ ml: 2 }} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => setIsEditing(true)}
                            sx={{
                              color: theme.palette.text.primary,
                              marginLeft: 1,
                            }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </>
                    </Stack>
                    
                  </Stack>




                </>
              )} */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0,
                mt: 0,

              }}
            >
              <Stack
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                gap={1}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Rampup Schedule
                </Typography>
                {loading && <CircularProgress size={15} sx={{ ml: 2 }} />}
                {!isBulkUpdate && !loading && !isEditing && mailboxAgeStatus === "loading" && (
                  <CircularProgress size={15} sx={{ ml: 1 }} />
                )}

                {!isBulkUpdate &&
                  !loading &&
                  !isEditing &&
                  mailboxAgeStatus !== "loading" && (
                  <IconButton
                    size="small"
                    onClick={() => setIsEditing(true)}
                    sx={{
                      color: theme.palette.text.primary,
                      ml: 1,
                    }}
                  >
                    <Edit />
                  </IconButton>
                )}
              </Stack>
              {/* {currentStep === 1 && ((isBulkUpdate) || (!isBulkUpdate && isEditing)) && (
                <Box sx={{ ml: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      reset();
                    }}
                    startIcon={<RestartAlt />}
                    sx={{
                      minWidth: "80px",
                      fontSize: "0.75rem",
                      padding: "4px 8px",
                    }}
                    className="glass-button-secondary-25"
                  >
                    Reset to Default
                  </Button>
                </Box>
              )} */}

            </Box>

            {!isBulkUpdate && !isEditing && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 3,
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                {/* {schedule?.map((value, index) => { */}
                {committedSchedule?.map((value, index) => {
                  const isDefault = index === dailyLimitInWeek;

                  return (
                    <Box
                      key={index}
                      sx={{ position: "relative", textAlign: "center" }}
                    >
                      <Tooltip
                        title="emails/day"
                        placement="top"
                        arrow
                      >
                        <TextField
                          sx={{
                            minWidth: "52px",
                            width: "max-content",
                            maxWidth: "76px",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "6px",
                              pointerEvents: "none",
                              backgroundColor: isDefault
                                ? theme.palette.primary.main
                                : theme.palette.action.disabledBackground,
                            },
                            "& .MuiOutlinedInput-input": {
                              p: 0,
                              px: 1,
                              height: "44px",
                              color: isDefault
                                ? theme.palette.primary.contrastText
                                : theme.palette.text.primary,
                              fontWeight: "700",
                              fontSize: "14px",
                              textAlign: "center",
                              userSelect: "none",
                            },
                          }}
                          value={value}
                          variant="outlined"
                          inputProps={{ readOnly: true }}
                        />
                      </Tooltip>
                      <Box
                        sx={{
                          fontSize: "12px",
                          color: theme.palette.text.secondary,
                          mt: 0.5,
                        }}
                      >
                        {`Day ${index + 1}`}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}


            {/* {!showRampPreview && ( */}
            {(isBulkUpdate || isEditing) && currentStep === 1 && (
              <Stack spacing={1} mt={0.5}>
                

                {rampStartEndEqual &&
                  !(
                    fixedDailySend !== "" &&
                    Number(fixedDailySend) === Number(startValue)
                  ) &&
                  !(
                    schedule.length === 1 &&
                    Number(schedule[0]) === Number(startValue) &&
                    Number(startValue) === Number(endValue)
                  ) && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    End must be greater than Start for a ramp. If they’re equal, it’s a flat limit—use “Fixed emails per day” or increase End value.                  
                  </Alert>
                )}

                {isRampLockedByAge && (
                  <Alert severity="info" sx={{ py: 0.75 }}>
                    This mailbox is 90+ days old, so the ramp is fixed (1 → {OLD_MAILBOX_END}, +{OLD_MAILBOX_INCREMENT}/day). The schedule has small ups and dips each day so sending stays human-like. You can still pick Daily or Weekly for step spacing.
                  </Alert>
                )}
                {!isBulkUpdate && mailboxAgeStatus === "loading" && isEditing && (
                  <Typography variant="caption" color="text.secondary">
                    Checking mailbox age…
                  </Typography>
                )}

                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ width: 120 }}>Start</Typography>
                  <TextField
                    value={startValue}
                    type="text"
                    inputMode="numeric"
                    size="small"
                    disabled={isRampLockedByAge}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw !== "" && !/^\d+$/.test(raw)) return;
                      if (raw === "") { setStartValue(""); return; }
                      const n = Number(raw);
                      if (n < 1) { setStartValue(1); return; }
                      if (n > 50) { setStartValue(50); return; }
                      setStartValue(n);
                      if (Number(endValue) <= n) {
                        const newEnd = Math.min(n + 1, 50);
                        setEndValue(newEnd);
                        setEndRawInput(String(newEnd));
                      }
                      setFixedDailySend("");
                    }}
                    onBlur={() => {
                      const n = Number(startValue);
                      if (!startValue || isNaN(n) || n < 1) {
                        setStartValue(1);
                      } else {
                        setStartValue(Math.round(n));
                      }
                      if (Number(endValue) <= n) {
                        const newEnd = Math.min(Math.round(n) + 1, 50);
                        setEndValue(newEnd);
                        setEndRawInput(String(newEnd));
                      }
                    }}
                    sx={{
                      width: 100,
                      '& .MuiInputBase-input': {
                        padding: '8px 8px',
                      }
                    }}
                    className="glass-input"
                  />
                </Stack>


                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ width: 120 }}>End</Typography>
                  <TextField
                    value={endRawInput}
                    type="text"
                    inputMode="numeric"
                    size="small"
                    disabled={isRampLockedByAge}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw !== "" && !/^\d+$/.test(raw)) return;
                      if (raw === "") { setEndRawInput(""); return; }
                      const n = Number(raw);
                      if (n > 50) {
                        setEndRawInput("50");
                        setEndValue(50);
                        return;
                      }
                      setEndRawInput(raw);
                      const minEnd = Math.max(2, Number(startValue) + 1);
                      if (n >= minEnd && n <= 50) {
                        setEndValue(n);
                      }
                      setFixedDailySend("");
                    }}
                    onBlur={() => {
                      const n = Number(endRawInput);
                      const minEnd = Math.max(2, Number(startValue) + 1);
                      if (!endRawInput || isNaN(n) || n < minEnd) {
                        setEndRawInput(String(minEnd));
                        setEndValue(minEnd);
                      } else {
                        const clamped = Math.min(n, 50);
                        setEndRawInput(String(clamped));
                        setEndValue(clamped);
                      }
                    }}
                    sx={{
                      width: 100,
                      '& .MuiInputBase-input': {
                        padding: '8px 8px',
                      }
                    }}
                    className="glass-input"
                  />
                  {/* <Typography>per day</Typography> */}
                </Stack>


                {/* Preference dropdown hidden for now — always sends as Daily */}
                {/* <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ width: 120 }}>Preference</Typography>
                  <Select
                    value={isWeekly ? "week" : "day"}
                    onChange={(e) => setIsWeekly(e.target.value === "week")}
                    size="small"
                    sx={{
                      width: 100,
                      '& .MuiInputBase-input': {
                        padding: '8px 8px',
                      }
                    }}
                    className="glass-dropdown"
                  >
                    <MenuItem value="day">Daily</MenuItem>
                    <MenuItem value="week">Weekly</MenuItem>
                  </Select>
                </Stack> */}

                {/* Current Value dropdown hidden for now */}
                {/* <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ width: 120 }}>Current Value</Typography>
                  <Select
                    disabled
                    value={
                      schedule.length > 0 &&
                      dailyLimitInWeek >= 0 &&
                      dailyLimitInWeek < schedule.length
                        ? dailyLimitInWeek
                        : 0
                    }
                    onChange={(e) => {
                      const idx = Number(e.target.value);
                      if (!Number.isFinite(idx) || idx < 0 || idx >= schedule.length) return;
                      setDailyLimitInWeek(idx);
                      setDailyLimit(schedule[idx]);
                      if (!isResetting) setMode("CUSTOM");
                    }}
                    size="small"
                    sx={{ width: 100 }}
                    InputProps={{
                      sx: {
                        py: 0.5,
                        px: 1
                      }
                    }}
                    className="glass-dropdown"
                    renderValue={(selectedIdx) =>
                      schedule[Number(selectedIdx)] ?? ""
                    }
                  >
                    {schedule.map((val, idx) => (
                      <MenuItem key={idx} value={idx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, }}>
                          <Typography sx={{ fontWeight: 600, minWidth: 20 }}>{val}</Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "12px" }}
                          >
                            {isWeekly ? `week ${idx + 1}` : `day ${idx + 1}`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Stack> */}

                <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
                  <Typography sx={{ width: 120, pt: 1 }}>Ramp-up Variance</Typography>
                  <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Select
                        value={
                          sendingVariancePercentage === ""
                            ? DEFAULT_SENDING_VARIANCE_PCT
                            : sendingVariancePercentage
                        }
                        onChange={(e) => setSendingVariancePercentage(Number(e.target.value))}
                        size="small"
                        sx={{
                          width: 100,
                          "& .MuiInputBase-input": {
                            padding: "8px 8px",
                          },
                        }}
                        className="glass-dropdown"
                      >
                        {VARIANCE_OPTIONS.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}%
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack spacing={0.25}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography sx={{ minWidth: 100, color: "text.secondary", fontSize: "13px" }}>
                      Est. complete
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "text.primary", fontWeight: 600 }}>
                      {formatRampEndDate(rampEndDatecal)}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: "100%", py: 0.5 }}
                >
                  <Typography 
                  sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}>
                    OR
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={2} >
                  <Typography>Fixed emails (no ramp)</Typography>
                  <TextField
                    value={fixedDailySend}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw !== "" && !/^\d+$/.test(raw)) return;
                      setFixedDailySend(raw);
                      if (raw === "") return;
                      const n = Number(raw);
                      if (n > 0) {
                        setStartValue(n);
                        setEndValue(n);
                        setIncreaseValue(1);
                        if (!isResetting) setMode("CUSTOM");
                      }
                    }}
                    placeholder="e.g. 4"
                    type="text"
                    inputMode="numeric"
                    size="small"
                    disabled={isRampLockedByAge}
                    sx={{
                      width: 100,
                      '& .MuiInputBase-input': {
                        padding: '8px 8px',
                      }
                    }}
                    className="glass-input"
                  />
                  {/* <Typography>per day</Typography> */}
                </Stack>
                <Stack>
                  <Typography sx={{ fontSize: "12px", color: theme.palette.text.secondary }}>
                    Sets Start and End to the same value (Increase = 1) for a flat daily limit. Leave empty if using a ramp up schedule.
                  </Typography>
                </Stack>

              </Stack>
            )}

            {/* {showRampPreview && ( */}
            {/* {currentStep === 2 && (

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    justifyContent: "center",
                    position: "relative",
                    marginLeft: "0%",
                    mt: 3
                  }}
                >
                  <IconButton
                    onClick={() => {
                      const newIndex = Math.max(0, startIndex - 6);
                      setStartIndex(newIndex);
                    }}
                    disabled={startIndex === 0}
                    sx={{
                      "&.Mui-disabled": {
                        opacity: 0.5,
                      },
                      mb: "25px",
                    }}
                  >
                    <ChevronLeft sx={{ color: theme.palette.text.primary }} />
                  </IconButton>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "11px",
                      position: "relative",
                    }}
                  >
                    {schedule
                      ?.slice(startIndex, startIndex + 6)
                      ?.map((value, index) => (
                        <React.Fragment key={index}>
                          <Box
                            sx={{ position: "relative", textAlign: "center" }}
                          >
                            <Tooltip
                              title={`${value} emails/day`}
                              placement="top"
                              arrow
                            >
                              <TextField
                                sx={{
                                  width: "60px",
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    p: 0,
                                    px: 1,
                                    height: "44px",
                                    border:
                                      startIndex + index === dailyLimitInWeek
                                        ? `2px solid ${theme.palette.primary.main}`
                                        : "none",
                                    backgroundColor:
                                      startIndex + index === dailyLimitInWeek
                                        ? theme.palette.primary.main
                                        : "none",
                                    color:
                                      startIndex + index === dailyLimitInWeek
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.text.primary,
                                    borderRadius: "inherit",
                                    cursor: "pointer",
                                    caretColor: "transparent",
                                  },
                                  "& input": {
                                    fontWeight: "700",
                                    color: theme.palette.text.primary,
                                    fontSize: "14px",
                                    textAlign: "center",
                                    userSelect: "none",
                                  },
                                }}
                                value={value}
                                onClick={() => {
                                  //console.log(value, "valuevaluevaluevalue");
                                  setDailyLimit(value);
                                  setDailyLimitInWeek(startIndex + index);
                                }}
                                onChange={(e) => { }}
                                variant="outlined"
                                className="glass-input"
                                inputProps={{
                                  readOnly: true,
                                }}
                              />
                            </Tooltip>
                            <Box
                              sx={{
                                fontSize: "12px",
                                color: theme.palette.text.secondary,
                                mt: 0.5,
                              }}
                            >
                              {`Day ${startIndex + index + 1}`}
                            </Box>
                          </Box>
                        </React.Fragment>
                      ))}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      onClick={() => {
                        const newIndex = Math.min(
                          schedule.length - 6,
                          startIndex + 6
                        );
                        setStartIndex(newIndex);
                      }}
                      disabled={startIndex + 6 >= schedule.length}
                      sx={{
                        "&.Mui-disabled": {
                          opacity: 0.5,
                        },
                        mb: "25px",
                      }}
                    >
                      <ChevronRight
                        sx={{ color: theme.palette.text.primary }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              )} */}
            {(isBulkUpdate || isEditing) && currentStep === 2 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: "12px",
                  mt: 3,
                }}
              >
                {schedule?.map((value, index) => (
                  <Box key={index} sx={{ textAlign: "center" }}>
                    <Tooltip
                      title="emails/day"
                      placement="top"
                      arrow
                    >
                      <TextField
                        sx={{
                          width: "60px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "1px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiOutlinedInput-input": {
                            p: 0,
                            px: 1,
                            height: "44px",
                            fontWeight: "700",
                            fontSize: "14px",
                            textAlign: "center",
                            backgroundColor:
                              index === dailyLimitInWeek
                                ? theme.palette.primary.main
                                : theme.palette.action.disabledBackground,
                            color:
                              index === dailyLimitInWeek
                                ? theme.palette.primary.contrastText
                                : theme.palette.text.primary,
                            cursor: "pointer",
                          },
                        }}
                        value={value}
                        onClick={() => {
                          setDailyLimit(value);
                          setDailyLimitInWeek(index);
                            if (!isResetting) setMode("CUSTOM");
                        }}
                        variant="outlined"
                        className="glass-input"
                        inputProps={{
                          readOnly: true,
                        }}
                      />
                    </Tooltip>
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: theme.palette.text.secondary,
                        mt: 0.5,
                      }}
                    >
                      {`Day ${index + 1}`}
                    </Box>
                  </Box>
                ))}

              </Box>
            )}


            {(isBulkUpdate || isEditing) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 0,
                  gap: 2,
                  mb: 1,
                  px: isBulkUpdate ? 1 : 1,
                  py: isBulkUpdate ? 1 : 1,
                }}
              >
                {currentStep === 1 && (
                  <>
                    {!isBulkUpdate && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const current = committedSchedule?.length ? committedSchedule : schedule;

                          setSchedule([...current]);
                          setDailyLimit(committedDailyLimit || dailyLimit);
                          setDailyLimitInWeek(committedDailyLimitInWeek);

                          setStartValue(current[0] ?? "");
                          setEndValue(current[current.length - 1] ?? "");
                          setIncreaseValue(
                            current.length >= 2
                              ? current[1] - current[0]
                              : DEFAULT_RAMP_INCREMENT
                          );
                          setSendingVariancePercentage(committedSendingVariancePercentage);

                          setIsEditing(false);
                          setStep1Completed(false);
                          // reset();
                          setCurrentStep(1);
                        }}
                        className="glass-button-secondary-50"
                      >
                        Cancel
                      </Button>
                    )}

                    <Button
                      variant="contained"
                      onClick={() => {
                        // Resolve effective end from raw input (handles case where user
                        // hasn't blurred the End field before clicking Next)
                        const rawN = Number(endRawInput);
                        const minEnd = Math.max(2, Number(startValue) + 1);
                        const effectiveEnd =
                          !endRawInput || isNaN(rawN) || rawN < minEnd
                            ? minEnd
                            : Math.min(rawN, 50);
                        setEndValue(effectiveEnd);

                        const scheduleList = buildRampSchedule(
                          1,
                          effectiveEnd,
                          DEFAULT_RAMP_INCREMENT,
                          rampScheduleSeed
                        );
                        setSchedule(scheduleList);
                        const clamped = Math.min(
                          Math.max(0, dailyLimitInWeek),
                          scheduleList.length - 1
                        );
                        setDailyLimitInWeek(clamped);
                        setDailyLimit(scheduleList[clamped] ?? scheduleList[0]);
                        setShowRampPreview(true);
                        setStep1Completed(true);
                        setCurrentStep(2);
                      }}
                      disabled={(() => {
                        const rawN = Number(endRawInput);
                        const minEnd = Math.max(2, Number(startValue) + 1);
                        const effectiveEnd =
                          !endRawInput || isNaN(rawN) || rawN < minEnd
                            ? minEnd
                            : Math.min(rawN, 50);
                        return effectiveEnd < minEnd;
                      })()}
                    >
                      Next
                    </Button>
                  </>
                )}

                {currentStep === 2 && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setCurrentStep(1);
                        setShowRampPreview(false);
                        setStep1Completed(false);
                      }}
                      className="glass-button-secondary-50"
                      sx={!isBulkUpdate ? {} : { mr: 0 }}
                    >
                      Back
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleSaveConfig}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : "Update"}
                    </Button>
                  </>
                )}
              </Box>
            )}
            {/* </Card> */}
          </Stack>
        </DialogContent>



      </Dialog>
    </>
  );
};

export default ConfigUpdate;
